import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { DataTable, type Column } from '@/components/ui/data-table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

import {
  useTokens,
  useCreateToken,
  useDeleteToken,
  type TokenRow,
} from '@/entities/userToken';

// Dialog: show newly created token
function DialogShowToken({
  token,
  onCopy,
  onClose,
}: {
  token: string | null
  onCopy: () => void
  onClose: () => void
}) {
  return (
    <Dialog open={!!token} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New token created</DialogTitle>
          <DialogDescription>
            Please save this token now. It will not be shown again.
          </DialogDescription>
        </DialogHeader>
        <div className="text-body break-all font-mono bg-gray-50 p-3 rounded border">
          {token}
        </div>
        <DialogFooter>
          <Button
            onClick={() => {
              if (token) navigator.clipboard?.writeText(token)
              onCopy()
            }}
            variant="secondary"
          >
            Copy
          </Button>
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// Dialog: confirm delete
function DialogConfirmDelete({
  open,
  onOpenChange,
  onConfirm,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete token</DialogTitle>
          <DialogDescription>
            This action cannot be undone. The token will be permanently deleted.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            className="bg-red-600 text-white hover:bg-red-700"
            onClick={onConfirm}
          >
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// Dialog: enter description before creating token
const EXPIRATION_OPTIONS = [
  { value: '', label: 'No expiration' },
  { value: '30', label: '30 days' },
  { value: '60', label: '60 days' },
  { value: '90', label: '90 days' },
  { value: '180', label: '180 days' },
  { value: '365', label: '1 year' },
]

function DialogCreateToken({
  open,
  onOpenChange,
  onConfirm,
  isPending,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (note: string, expiresAt?: string) => void
  isPending: boolean
}) {
  const [note, setNote] = useState('')
  const [expiration, setExpiration] = useState('')

  const reset = () => { setNote(''); setExpiration('') }

  const getExpiresAt = (): string | undefined => {
    if (!expiration) return undefined
    const d = new Date()
    d.setDate(d.getDate() + parseInt(expiration, 10))
    return d.toISOString()
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v) }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create API token</DialogTitle>
          <DialogDescription>
            Add an optional description and expiration to help manage this token.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="token-note">Description</Label>
            <Input
              id="token-note"
              placeholder="e.g. Production dialer, Staging tests..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={127}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="token-expiration">Expiration</Label>
            <select
              id="token-expiration"
              value={expiration}
              onChange={(e) => setExpiration(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-body ring-offset-background"
            >
              {EXPIRATION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => { reset(); onOpenChange(false) }}>
            Cancel
          </Button>
          <Button
            onClick={() => { onConfirm(note, getExpiresAt()); reset() }}
            disabled={isPending}
          >
            {isPending ? 'Creating...' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function ApiToken() {
  const { data: tokens = [], isLoading } = useTokens()
  const createToken = useCreateToken()
  const deleteToken = useDeleteToken()

  const [createdToken, setCreatedToken] = useState<string | null>(null)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [tokenToDelete, setTokenToDelete] = useState<string | null>(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)

  const columns: Column<TokenRow>[] = useMemo(
    () => [
      {
        header: 'Secret key',
        accessorKey: 'token_alias',
        sortable: false,
        cell: (v) => {
          const alias = v as string | null
          return alias ? `****${alias}` : '—'
        },
        width: { min: 80 },
      },
      {
        header: 'Description',
        accessorKey: 'note',
        sortable: false,
        cell: (v) => (v as string) || '—',
      },
      {
        header: 'Created',
        accessorKey: 'created_at',
        sortable: false,
        cell: (value) => (
          <>
            {new Date(value as string).toLocaleDateString()}{' '}
            {new Date(value as string).toLocaleTimeString()}
          </>
        ),
        width: { min: 160 },
      },
      {
        header: 'Last used',
        accessorKey: 'last_used_at',
        sortable: false,
        cell: (value) => {
          if (!value) return <span className="text-gray-400">Never</span>
          return new Date(value as string).toLocaleDateString()
        },
        width: { min: 100 },
      },
      {
        header: 'Expires',
        accessorKey: 'expires_at',
        sortable: false,
        cell: (value) => {
          if (!value) return <span className="text-gray-400">Never</span>
          const d = new Date(value as string)
          const isExpired = d < new Date()
          return (
            <span className={isExpired ? 'text-red-600 font-medium' : ''}>
              {isExpired ? 'Expired ' : ''}{d.toLocaleDateString()}
            </span>
          )
        },
        width: { min: 110 },
      },
      {
        header: 'Author',
        accessorKey: 'email',
        sortable: false,
        cell: (value, row) => (value as string) || row.clerk_user_id || '—',
      },
    ],
    []
  )

  const actions = (row: TokenRow) => (
    <Button
      variant="ghost"
      onClick={() => handleDelete(row.token_hash)}
      className="text-red-500"
      title="Delete Token"
    >
      <Trash2 className="w-2.5 h-2.5" />
    </Button>
  )

  async function handleCreate(note: string, expiresAt?: string) {
    try {
      const result = await createToken.mutateAsync({ note, expires_at: expiresAt })
      setCreatedToken(result.token)
      setCreateDialogOpen(false)
    } catch {
      toast.error('Failed to create token')
    }
  }

  function handleDelete(token_hash: string) {
    setTokenToDelete(token_hash)
    setConfirmDeleteOpen(true)
  }

  function confirmDelete() {
    if (!tokenToDelete) return
    deleteToken.mutate(tokenToDelete, {
      onSuccess: () => {
        toast.success('Token deleted')
      },
      onError: () => {
        toast.error('Failed to delete token')
      },
    })
    setConfirmDeleteOpen(false)
    setTokenToDelete(null)
  }

  return (
    <div className="p-6">
      <h1 className="text-heading font-semibold mb-2">API token</h1>
      <p className="text-body text-gray-600 mb-4">
        Manage organization API tokens. You can create multiple tokens per
        organization.
      </p>

      <Button onClick={() => setCreateDialogOpen(true)} disabled={createToken.isPending}>
        Create token
      </Button>

      <div className="mt-6">
        {isLoading ? (
          <p className="text-gray-500">Loading tokens...</p>
        ) : (
          <DataTable<TokenRow>
            columns={columns}
            data={tokens as TokenRow[]}
            actions={actions}
          />
        )}
      </div>

      {/* Dialog: enter description */}
      <DialogCreateToken
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onConfirm={handleCreate}
        isPending={createToken.isPending}
      />

      {/* Dialog: show newly created token only once */}
      <DialogShowToken
        token={createdToken}
        onCopy={() => toast.success('Token copied to clipboard')}
        onClose={() => setCreatedToken(null)}
      />

      {/* Dialog: confirm delete */}
      <DialogConfirmDelete
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        onConfirm={confirmDelete}
      />
    </div>
  )
}